/**
 * Beyond Sure — Launch-2 chat: REAL API client (C2).
 *
 * Streams the answer envelope from POST /api/chat/answer/stream (SSE). This is the production swap
 * for streamingMock.ts: ChatScreen replaces `mockStream(...)` with `streamChatAnswer(...)` — same
 * onDelta/onDone shape, real backend. The server FORCES Strict on financial-intent queries (the
 * `forced_strict` badge), grounds the answer in the Deepak brain (citations + confidence), and
 * degrades honestly to `{available:false, reason:"BLOCKED-needs-key"}` when no AI provider is wired.
 *
 * Contract (mirrors server.py /api/chat/answer): proven by eval/test_chat_envelope.py (20/20).
 */

import type { Message } from "./types";

export type ChatMode = "strict" | "creative";

export interface ChatCitation {
  kb_id: number | string | null;
  source: string;
  tier: "confirmed" | "provisional" | string;
}

export interface ChatConfidence {
  score: number;            // 0..1
  label: "high" | "medium" | "low";
  basis: string;
}

export interface ChatBrainTrace {
  rules_hit: string[];
  persona: string;          // "deepak"
  grounding: string | null; // confirmed | provisional | live_evidence | live_signal | llm-only
  retrieval: { sparse_n: number; dense_n: number };
}

export interface ChatEnvelope {
  answer: string | null;
  citations: ChatCitation[];
  confidence: ChatConfidence;
  mode: ChatMode;
  forced_strict: boolean;
  model: string | null;
  provider?: string;
  brain_trace: ChatBrainTrace;
  strict_reason?: string;
  available: boolean;
  reason?: string;          // present when available === false (e.g. "BLOCKED-needs-key: ...")
  escalated?: boolean;      // M2: routed to the Expert Console (low-confidence/gated)
  review_id?: number | string | null;
  holding?: boolean;        // the shown answer is a holding answer pending expert confirmation
  render?: string | null;   // BI2: "steps" → the UI renders `steps` as a visual step-flow
  steps?: string[];         // BI2: verbatim grounded process steps lifted from the answer
  conversation_id?: string | null;  // BQ7-2: the persisted conversation id (thread continuity + CSAT)
}

export interface StreamChatCallbacks {
  onDelta?: (textChunk: string) => void;
  onDone?: (envelope: ChatEnvelope) => void;
  onUnavailable?: (envelope: ChatEnvelope) => void; // honest no-key gate
  onError?: (err: unknown) => void;
}

export interface StreamChatOptions {
  query: string;
  mode?: ChatMode;          // client toggle; server overrides to Strict on financial intent
  model?: string;          // optional preferred model key
  conversationId?: string; // BQ7-2: continue an existing thread (server owner-verifies it)
  token?: string;          // Bearer token; falls back to localStorage if omitted
  signal?: AbortSignal;
}

function authHeader(token?: string): Record<string, string> {
  const t =
    token ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("bsure_token") || localStorage.getItem("token") || ""
      : "");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Stream a chat answer. Resolves when the stream completes (after onDone/onUnavailable fire).
 * Parses the SSE frames emitted by /api/chat/answer/stream: `chunk` (token text) and `done`
 * (final envelope), plus `error` (honest unavailable gate).
 */
export async function streamChatAnswer(opts: StreamChatOptions, cb: StreamChatCallbacks): Promise<void> {
  const { query, mode, model, token, signal } = opts;
  try {
    const resp = await fetch("/api/chat/answer/stream", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ query, mode, model, conversation_id: opts.conversationId }),
      signal,
    });

    if (!resp.ok && resp.status !== 200) {
      // 401/400 etc. — surface the JSON error if any.
      let detail: unknown = null;
      try {
        detail = await resp.json();
      } catch {
        /* non-JSON */
      }
      cb.onError?.(detail ?? new Error(`chat stream HTTP ${resp.status}`));
      return;
    }
    if (!resp.body) {
      cb.onError?.(new Error("chat stream: no response body"));
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        let evt: { type?: string; text?: string; envelope?: ChatEnvelope; reason?: string };
        try {
          evt = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (evt.type === "chunk" && evt.text) {
          cb.onDelta?.(evt.text);
        } else if (evt.type === "done" && evt.envelope) {
          cb.onDone?.(evt.envelope);
        } else if (evt.type === "error" && evt.envelope) {
          cb.onUnavailable?.(evt.envelope);
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    cb.onError?.(err);
  }
}

export interface FeedbackInput {
  messageId: string;
  thumb: "up" | "down";
  comment?: string;       // the "suggestion" — server requires >= 10 chars on a down-vote with comment
  surface?: string;       // default "chat"
  token?: string;
}

/**
 * L04 capture — POST /api/feedback (the C2 training-flywheel pattern). A thumbs-down with a suggestion
 * feeds the correction pipeline. Returns the server error code on failure (e.g. "comment_min_length")
 * so the UI can guide the user honestly rather than swallowing it.
 */
export async function postFeedback(inp: FeedbackInput): Promise<{ ok: boolean; error?: string }> {
  try {
    const resp = await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(inp.token) },
      body: JSON.stringify({
        message_id: inp.messageId,
        thumb: inp.thumb,
        comment: inp.comment,
        surface: inp.surface || "chat",
      }),
    });
    if (resp.ok) return { ok: true };
    let detail: { error?: string; reason?: string } | null = null;
    try {
      detail = await resp.json();
    } catch {
      /* non-JSON */
    }
    return { ok: false, error: (detail && (detail.error || detail.reason)) || `HTTP ${resp.status}` };
  } catch {
    return { ok: false, error: "network" };
  }
}

/** Non-streaming variant — POST /api/chat/answer. Returns the full envelope (or the gate). */
export async function fetchChatAnswer(opts: StreamChatOptions): Promise<ChatEnvelope> {
  const { query, mode, model, token, signal } = opts;
  const resp = await fetch("/api/chat/answer", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(token) },
    body: JSON.stringify({ query, mode, model, conversation_id: opts.conversationId }),
    signal,
  });
  return (await resp.json()) as ChatEnvelope;
}

interface HistoryConversationRow {
  id: string;
  title: string;
  updated_at: string; // SQLite `datetime('now')` — UTC, "YYYY-MM-DD HH:MM:SS"
}

/** Parses SQLite's `datetime('now')` output (UTC, no timezone suffix) into epoch ms. */
function parseSqlUtcDatetime(s: string): number {
  const iso = s.includes("T") ? s : `${s.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Date.now() : ms;
}

/**
 * Real chat history for the sidebar — GET /api/history, owner-scoped server-side. Replaces the
 * hardcoded MOCK_SEED_CONVERSATIONS that previously showed every user (including brand-new
 * accounts with zero real activity) the same 5 fake threads. Best-effort: an empty/failed
 * response degrades to no conversations, never fake ones.
 */
export async function fetchConversationHistory(
  token?: string,
): Promise<{ id: string; title: string; lastActiveAt: number }[]> {
  try {
    const resp = await fetch("/api/history", {
      headers: { ...authHeader(token) },
    });
    if (!resp.ok) return [];
    const data = (await resp.json()) as { conversations?: HistoryConversationRow[] };
    return (data.conversations || []).map((c) => ({
      id: c.id,
      title: c.title || "New chat",
      lastActiveAt: parseSqlUtcDatetime(c.updated_at),
    }));
  } catch {
    return [];
  }
}

interface HistoryMessageRow {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  feedback?: string | null;
  has_attachment?: number;
  model_used?: string | null;
  created_at: string; // SQLite `datetime('now')` — UTC, "YYYY-MM-DD HH:MM:SS"
}

/**
 * BF4 — the read-back half of BUGFIX-1: fetch a saved conversation's messages so clicking it in
 * the sidebar actually loads the thread, not just highlights it. GET /api/history/<id>,
 * owner-scoped server-side (403 + empty on a conversation that isn't the caller's — safely covered
 * here by degrading to [] on any non-200, same pattern as fetchConversationHistory).
 */
export async function fetchConversationMessages(
  conversationId: string,
  token?: string,
): Promise<Message[]> {
  try {
    const resp = await fetch(`/api/history/${encodeURIComponent(conversationId)}`, {
      headers: { ...authHeader(token) },
    });
    if (!resp.ok) return [];
    const data = (await resp.json()) as { messages?: HistoryMessageRow[] };
    return (data.messages || []).map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: parseSqlUtcDatetime(m.created_at),
    }));
  } catch {
    return [];
  }
}

/**
 * BQ7-2 — submit a conversation-level / post-claims CSAT ("was this helpful?"). Owner-scoped on the
 * server (the conversation must belong to the caller). rating 0–5 (or 0/1 for a yes/no). Best-effort.
 */
export async function postConversationCsat(
  conversationId: string,
  rating: number,
  opts?: { context?: string; comment?: string; token?: string },
): Promise<{ ok: boolean; error?: string }> {
  try {
    const resp = await fetch(`/api/conversation/${encodeURIComponent(conversationId)}/csat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(opts?.token) },
      body: JSON.stringify({ rating, context: opts?.context || "end_of_conversation", comment: opts?.comment }),
    });
    if (resp.ok) return { ok: true };
    let detail: { error?: string } | null = null;
    try { detail = await resp.json(); } catch { /* non-JSON */ }
    return { ok: false, error: (detail && detail.error) || `HTTP ${resp.status}` };
  } catch {
    return { ok: false, error: "network" };
  }
}

export interface UploadResult {
  ok: boolean;
  error?: string;
  summary?: string;
  fields?: Record<string, unknown>;
  partial?: boolean;      // BI1: read succeeded but couldn't fully structure the doc (still grounded)
  partialNote?: string;   // BI1: human note about what was partial, shown to the user
}

function summarizeFields(f: Record<string, unknown>): string {
  if (!f || typeof f !== "object") return "";
  const pick = (k: string) => (f[k] != null ? String(f[k]) : "");
  const insurer = pick("insurer") || pick("company_name") || pick("insurer_name");
  const si = pick("sum_insured") || pick("sum_assured");
  return [insurer, si && `cover ${si}`].filter(Boolean).join(" · ");
}

/**
 * AH1 — upload a policy document from the chat composer to the OCR Pro pipeline (POST /api/upload,
 * multipart field `policy`). The server extracts the document and persists it to ocr_extractions_v2
 * scoped to the user, so the NEXT chat turn injects the extraction (server get_chat_grounding_ctx)
 * and the bot audits the real fields instead of refusing "I'm unable to review documents directly".
 * Returns {ok:false} honestly on failure — never fabricates an extraction.
 */
export async function uploadChatDocument(file: File, token?: string): Promise<UploadResult> {
  try {
    const fd = new FormData();
    fd.append("policy", file, file.name);
    const resp = await fetch("/api/upload", {
      method: "POST",
      headers: { ...authHeader(token) }, // NO Content-Type — browser sets the multipart boundary
      body: fd,
    });
    let data: Record<string, unknown> = {};
    try {
      data = (await resp.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON */
    }
    if (!resp.ok) {
      return { ok: false, error: String((data && (data.error || data.reason)) || `HTTP ${resp.status}`) };
    }
    // BI1: the server returns the extraction under `data` (not `result`/`extracted_data`), so read
    // that first — the summary now reflects real fields — and pass through the partial-read signal.
    const fields = (data.data || data.result || data.extracted_data || data) as Record<string, unknown>;
    return {
      ok: true,
      fields,
      summary: summarizeFields(fields),
      partial: Boolean(data.partial),
      partialNote: typeof data.partial_note === "string" ? data.partial_note : undefined,
    };
  } catch {
    return { ok: false, error: "network" };
  }
}
